import React from "react";
import BrowserFrame from "@/components/BrowserFrame";
import {
  LayoutGrid, Calendar, Globe2, Users, Settings2, BarChart3, Sparkles,
  Bell, Search, Plus, CheckCircle2,
} from "lucide-react";

/**
 * Cinematic dashboard mockup — full PMS application shell:
 * sidebar + topbar + reservations grid + right panel.
 * Pure HTML/Tailwind. Replaces the prior compact mockup.
 */
export default function DashboardMockup({ compact = false }) {
  const rooms = [
    { n: "101", t: "Standard" },
    { n: "102", t: "Standard" },
    { n: "103", t: "Standard" },
    { n: "201", t: "Deluxe" },
    { n: "202", t: "Deluxe" },
    { n: "301", t: "Suite" },
    { n: "302", t: "Suite" },
  ];
  const days = ["Hën 18", "Mar 19", "Mër 20", "Enj 21", "Pre 22", "Sht 23", "Die 24"];
  const bookings = [
    [0, 0, 3, "M. Hoxha",   "Booking.com", "#003580", "#E8EEF8"],
    [0, 4, 2, "A. Krasniqi","Direkt",      "#16A34A", "#DCFCE7"],
    [1, 1, 4, "L. Mema",    "Airbnb",      "#FF5A5F", "#FFE4E6"],
    [2, 2, 5, "E. Berisha", "Expedia",     "#0072CE", "#DBEAFE"],
    [3, 0, 2, "I. Tafa",    "Booking.com", "#003580", "#E8EEF8"],
    [3, 3, 4, "G. Doci",    "Direkt",      "#16A34A", "#DCFCE7"],
    [4, 5, 2, "F. Lleshi",  "Agoda",       "#5392F9", "#DBEAFE"],
    [5, 1, 6, "R. Pano",    "Direkt",      "#16A34A", "#DCFCE7"],
    [6, 2, 4, "S. Kola",    "Vrbo",        "#245ABF", "#DBEAFE"],
  ];

  return (
    <BrowserFrame url="app.akomoda.co/rezervimet" title="PMS · Java 47" lg>
      <div className="grid grid-cols-[180px_1fr] bg-white">
        {/* Sidebar */}
        <aside className="hidden border-r border-[#E2E8F0] bg-[#FAFBFC] p-3 sm:block">
          <div className="mb-4 flex items-center gap-2 px-1.5">
            <span className="grid h-7 w-7 place-items-center rounded-md bg-[#FF7A00] font-display text-sm font-extrabold text-white">a</span>
            <span className="font-display text-[13px] font-extrabold tracking-tight text-[#0F172A]">akomoda</span>
          </div>
          <nav className="space-y-0.5">
            {[
              [Calendar, "Rezervimet", true],
              [LayoutGrid, "Dhomat", false],
              [Globe2, "Channel Manager", false],
              [Users, "Klientët", false],
              [BarChart3, "Raporte", false],
              [Sparkles, "Të Ardhurat", false],
              [Settings2, "Cilësimet", false],
            ].map(([Icon, label, active]) => (
              <div key={label} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[12px] ${active ? "bg-[#FFF1E2] text-[#EA580C] font-semibold" : "text-[#475569]"}`}>
                <Icon size={13} /> {label}
              </div>
            ))}
          </nav>
          <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-white p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Pronat</div>
            <div className="mt-1.5 text-[12px] font-semibold text-[#0F172A]">Hotel Onufri</div>
            <div className="text-[10px] text-[#64748B]">7 dhoma · Tiranë</div>
          </div>
        </aside>

        {/* Main */}
        <div>
          {/* Topbar */}
          <div className="flex items-center justify-between border-b border-[#E2E8F0] px-4 py-2.5">
            <div className="flex items-center gap-2 text-[12px]">
              <span className="font-display text-[14px] font-bold text-[#0F172A]">Rezervimet</span>
              <span className="hidden text-[#94A3B8] sm:inline">/ Java 47 · 18–24 Nëntor</span>
            </div>
            <div className="flex items-center gap-2 text-[12px]">
              <span className="hidden items-center gap-1.5 rounded-md border border-[#E2E8F0] bg-white px-2 py-1 text-[#64748B] sm:inline-flex">
                <Search size={11} /> Kërko klient…
              </span>
              <span className="hidden items-center gap-1.5 rounded-md border border-[#16A34A]/20 bg-[#DCFCE7] px-2 py-1 text-[11px] font-semibold text-[#166534] sm:inline-flex">
                <span className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" /> Sinkron OTA
              </span>
              <span className="grid h-7 w-7 place-items-center rounded-md border border-[#E2E8F0]"><Bell size={12} className="text-[#64748B]" /></span>
              <span className="inline-flex items-center gap-1 rounded-md bg-[#0F172A] px-2.5 py-1 text-[11px] font-semibold text-white"><Plus size={11} /> Rezervim</span>
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-[88px_repeat(7,minmax(0,1fr))] text-[10.5px]">
            <div className="border-b border-r border-[#E2E8F0] bg-[#FAFBFC] px-2 py-2 font-semibold text-[#64748B]">Dhoma</div>
            {days.map((d, i) => (
              <div key={d} className={`border-b border-[#E2E8F0] bg-[#FAFBFC] px-2 py-2 text-center font-semibold text-[#64748B] ${i === 6 ? "" : "border-r"}`}>
                {d}
              </div>
            ))}

            {rooms.map((r, ri) => (
              <React.Fragment key={r.n}>
                <div className="border-b border-r border-[#E2E8F0] px-2 py-2.5">
                  <div className="text-[11px] font-bold text-[#0F172A]">{r.n}</div>
                  <div className="text-[9px] text-[#94A3B8]">{r.t}</div>
                </div>
                {days.map((_, di) => {
                  const book = bookings.find(([rr, sd, sp]) => rr === ri && di >= sd && di < sd + sp);
                  const isStart = book && book[1] === di;
                  return (
                    <div key={di} className={`relative border-b border-[#E2E8F0] ${di === 6 ? "" : "border-r"} cal-cell h-11`}>
                      {isStart && (
                        <div
                          className="absolute inset-y-1.5 left-1 flex items-center overflow-hidden rounded-md px-1.5 text-[9.5px] font-semibold leading-tight"
                          style={{ width: `calc(${book[2] * 100}% - 8px)`, background: book[6], color: book[5] }}
                        >
                          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full" style={{ background: book[5] }} />
                          <span className="truncate">{book[3]}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          {/* KPI footer */}
          <div className="grid grid-cols-2 gap-px border-t border-[#E2E8F0] bg-[#E2E8F0] text-[11px] sm:grid-cols-4">
            {[
              ["Okupanca", "84%", "+6.4%", "#16A34A"],
              ["ADR", "€72.40", "+3.1%", "#16A34A"],
              ["RevPAR", "€60.81", "+9.7%", "#16A34A"],
              ["Sinkron OTA", "12s", "ago", "#64748B"],
            ].map(([k, v, d, c]) => (
              <div key={k} className="flex flex-col bg-white px-3.5 py-3">
                <span className="text-[10px] uppercase tracking-wider text-[#64748B]">{k}</span>
                <span className="mt-1 flex items-baseline gap-2">
                  <span className="font-display text-[16px] font-extrabold text-[#0F172A]">{v}</span>
                  <span className="text-[10px] font-semibold" style={{ color: c }}>{d}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating notification card */}
      {!compact && (
        <div className="pointer-events-none absolute -bottom-6 -right-6 hidden w-[260px] rotate-[-2deg] md:block">
          <div className="card-elevated p-3.5">
            <div className="flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-md bg-[#DCFCE7] text-[#166534]"><CheckCircle2 size={14} /></span>
              <div className="flex-1">
                <div className="text-[12px] font-semibold text-[#0F172A]">Rezervim i ri · Airbnb</div>
                <div className="text-[11px] text-[#64748B]">L. Mema · 3 net · €216</div>
              </div>
            </div>
            <div className="mt-2.5 flex items-center justify-between border-t border-[#E2E8F0] pt-2 text-[10px]">
              <span className="text-[#64748B]">Sinkron në 1.2s</span>
              <span className="pill pill-success">Konfirmuar</span>
            </div>
          </div>
        </div>
      )}
    </BrowserFrame>
  );
}
